import React, { useState, useEffect } from 'react';
import './SatelliteAnalysis.css';

function SatelliteAnalysis({ onBack, onSubmitVerification }) {
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(true);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    // Simulate processing progress
    if (isProcessing) {
      const interval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsProcessing(false);
            setTimeout(() => setShowResults(true), 500);
            return 100;
          }
          return prev + 2;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [isProcessing]);

  const handleSubmitVerification = () => {
    console.log('Submitting for verification...');
    // Navigate to verification portal
    if (onSubmitVerification) {
      onSubmitVerification();
    }
  };

  return (
    <div className="satellite-analysis">
      {/* Header */}
      <header className="analysis-header">
        <div className="header-content">
          <h1 className="analysis-title">Satellite Analysis</h1>
          <p className="analysis-subtitle">AI-Powered Carbon Assessment</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="analysis-container">
        {/* Processing Status Section */}
        <section className="analysis-section">
          <div className="section-header">
            <span className="section-icon">⚙️</span>
            <span className="section-title">Processing Status</span>
          </div>
          <div className="processing-content">
            <div className="status-text">
              {isProcessing ? 'Processing...' : 'Analysis complete'}
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill"
                style={{ width: `${processingProgress}%` }}
              >
                <span className="progress-text">{processingProgress}% complete</span>
              </div>
            </div>
          </div>
        </section>

        {/* Satellite Data Section */}
        <section className={`analysis-section fade-in ${showResults ? 'visible' : ''}`}>
          <div className="section-header">
            <span className="section-icon">🛰️</span>
            <span className="section-title">Satellite Data</span>
          </div>
          <div className="satellite-data-content">
            <div className="satellite-visualization">
              <div className="gradient-map">
                <div className="map-overlay">
                  <div className="coverage-badge">Mangrove Coverage: 74%</div>
                </div>
                <div className="map-legend">
                  <div className="legend-item">
                    <span className="legend-color" style={{ background: '#2ecc71' }}></span>
                    <span>High Density</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color" style={{ background: '#3498db' }}></span>
                    <span>Medium Density</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color" style={{ background: '#95a5a6' }}></span>
                    <span>Low Density</span>
                  </div>
                </div>
              </div>
              <div className="data-info">
                <p><strong>Resolution:</strong> 10m/pixel | <strong>Source:</strong> Sentinel-2</p>
              </div>
            </div>
          </div>
        </section>

        {/* Analysis Results Section */}
        <section className={`analysis-section fade-in ${showResults ? 'visible' : ''}`}>
          <div className="section-header">
            <span className="section-icon">📊</span>
            <span className="section-title">Analysis Results</span>
          </div>
          <div className="results-grid">
            <div className="result-card animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="result-value">2.3</div>
              <div className="result-label">tons CO₂</div>
              <div className="result-sublabel">Vegetation Coverage</div>
            </div>
            <div className="result-card animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="result-value">1.2</div>
              <div className="result-label">hectares</div>
              <div className="result-sublabel">Health Score</div>
            </div>
          </div>
          <div className="metrics-list">
            <div className="metric-item">
              <span className="metric-label">Vegetation Coverage</span>
              <span className="metric-value">74%</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Health Score</span>
              <span className="metric-value">Excellent</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Biodiversity Index</span>
              <span className="metric-value">85/100</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Soil Quality</span>
              <span className="metric-value">Good</span>
            </div>
          </div>
        </section>

        {/* Analysis Complete Section */}
        <section className={`analysis-section fade-in ${showResults ? 'visible' : ''}`}>
          <div className="section-header">
            <span className="section-icon">✅</span>
            <span className="section-title">Analysis Complete</span>
          </div>
          <div className="complete-content">
            <p className="complete-message">
              AI analysis complete with 94% confidence. Ready for expert verification.
            </p>
            <div className="quality-badge">
              <span className="badge-icon">⭐</span>
              <span className="badge-text">High quality data detected</span>
            </div>
          </div>
        </section>

        {/* Submit Button */}
        <div className={`submit-section fade-in ${showResults ? 'visible' : ''}`}>
          <button 
            className="submit-verification-btn"
            onClick={handleSubmitVerification}
            disabled={isProcessing}
          >
            <span className="btn-icon">✓</span>
            Submit for Verification
          </button>
        </div>
      </div>
    </div>
  );
}

export default SatelliteAnalysis;
